import { FUNDUS_IMAGES, EYE_EXTERIOR_IMAGES } from 'store/examinations';

import {
  URL,
  API_URL,
  POST,
  OWN_ERR_HANDLER,
  DELETE,
  FILE_URL,
  FILE_URL_EXPIRES,
  OFFLINE,
  FILE_ID,
  BUCKET,
  MIMETYPE,
} from './consts';
import { wrapperRequest, getToken } from './utils';
import { getCache, UPLOAD_FILES_CACHE, GOOGLE_CLOUD_CACHE } from './cache';

const urlCreator = window.URL || window.webkitURL;

export const uploadFile = (file) => {
  const data = new FormData();
  data.append('file', file);

  return wrapperRequest({
    url: `${API_URL}/uploads`,
    options: {
      method: POST,
      headers: { AUTHORIZATION: `Bearer ${getToken()}` },
      file: data,
    },
  });
};

export const uploadXMLFile = (xmlFile) => {
  const data = new FormData();
  data.append('file', xmlFile);

  return wrapperRequest({
    url: `${API_URL}/visualacuities`,
    options: {
      method: POST,
      headers: { AUTHORIZATION: `Bearer ${getToken()}` },
      file: data,
    },
  });
};

export const uploadGCloudFile = async (file, bucket) => {
  const formData = new FormData();
  formData.append('file', file);

  return wrapperRequest({
    url: `${API_URL}/buckets/${bucket}`,
    options: {
      method: POST,
      headers: { AUTHORIZATION: `Bearer ${getToken()}` },
      file: formData,
    },
  });
};

export const uploadSearchableMedia = (file, patientId) =>
  wrapperRequest({
    url: `${API_URL}/patients/${patientId}/searchablemedia`,
    options: {
      method: POST,
      body: { file },
      [OWN_ERR_HANDLER]: true,
    },
  });

const loadGCloudFile = async (fileData) => {
  const { [FILE_ID]: id, [FILE_URL]: url, [FILE_URL_EXPIRES]: urlExpires, [BUCKET]: bucket } = fileData;
  const now = Date.now();

  if (url && urlExpires && new Date(urlExpires).valueOf() > now) return url;

  const gcCache = await getCache(GOOGLE_CLOUD_CACHE);
  const cacheUrl = `${URL}/${GOOGLE_CLOUD_CACHE}/${id}`;
  const cacheData = await gcCache.match(cacheUrl).then((r) => (r ? r.json() : {}));

  if (cacheData[FILE_URL] && cacheData[FILE_URL_EXPIRES] && new Date(cacheData[FILE_URL_EXPIRES]).valueOf() > now) {
    return cacheData[FILE_URL];
  }

  const data = await wrapperRequest({
    url: `${API_URL}/buckets/${bucket}/${id}`,
    options: {
      method: POST,
      headers: { AUTHORIZATION: `Bearer ${getToken()}` },
      body: {},
    },
  });

  await gcCache.put(cacheUrl, new Response(JSON.stringify(data)));

  return data.url;
};

export const loadFile = async (fileData) => {
  try {
    let response;
    if (fileData[OFFLINE]) {
      const cache = await getCache(UPLOAD_FILES_CACHE);

      response = cache ? await cache.match(fileData[FILE_URL]) : '';
    } else if (fileData[FILE_ID] && fileData[BUCKET]) {
      return await loadGCloudFile(fileData);
    } else {
      response = await fetch(`${URL}/${fileData[FILE_URL]}`, { 'Content-Type': fileData[MIMETYPE] });
    }

    if (!response) return '';

    const blob = await response.blob();
    const blobUrl = await urlCreator.createObjectURL(blob);

    return blobUrl;
  } catch (e) {
    console.error('Image could not be loaded.'); // eslint-disable-line no-console
    return '';
  }
};

export const deleteFileFromGCloud = async (fileData) => {
  const name =
    (fileData[BUCKET] === EYE_EXTERIOR_IMAGES && 'eye_exterior') ||
    (fileData[BUCKET] === FUNDUS_IMAGES && 'fundus') ||
    fileData[BUCKET];
  const data = await wrapperRequest({
    url: `${API_URL}/buckets/${name}/${fileData[FILE_ID]}`,
    options: { method: DELETE },
  });

  await getCache(GOOGLE_CLOUD_CACHE).then(($) => $.delete(`${URL}/${GOOGLE_CLOUD_CACHE}/${fileData[FILE_ID]}`));

  return data;
};
